import type { IsoDate, Weekday } from './types.js';

/** getUTCDay() is 0-indexed from Sunday. */
const BY_UTC_DAY: readonly Weekday[] = [
  'sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat',
] as const;

const MONTHS: readonly string[] = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
] as const;

const DAY_NAMES: readonly string[] = [
  'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday',
] as const;

const MS_PER_DAY = 86_400_000;

/**
 * The only way to make an `IsoDate`.
 *
 * Validates shape AND that the date exists (2026-02-30 parses as a shape but is
 * not a real day). Everything downstream can then assume its dates are real,
 * and no UK- or US-formatted string can reach a field expecting ISO.
 */
export function isoDate(value: string): IsoDate {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    throw new Error(`Not an ISO date (yyyy-MM-dd): "${value}"`);
  }
  const parsed = new Date(`${value}T00:00:00.000Z`);
  if (Number.isNaN(parsed.getTime()) || parsed.toISOString().slice(0, 10) !== value) {
    throw new Error(`"${value}" is not a real date`);
  }
  return value as IsoDate;
}

function toUtc(date: IsoDate): Date {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d));
}

function toIso(d: Date): IsoDate {
  return d.toISOString().slice(0, 10) as IsoDate;
}

export function weekdayOf(date: IsoDate): Weekday {
  const day = BY_UTC_DAY[toUtc(date).getUTCDay()];
  if (!day) throw new Error(`Unreachable weekday for "${date}"`);
  return day;
}

export function addDays(date: IsoDate, n: number): IsoDate {
  return toIso(new Date(toUtc(date).getTime() + n * MS_PER_DAY));
}

export function weekDates(weekStart: IsoDate): IsoDate[] {
  if (weekdayOf(weekStart) !== 'mon') {
    throw new Error(`weekStart must be a Monday, got ${formatUk(weekStart)}`);
  }
  return Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
}

/**
 * UK long form. Every date a person reads is UK format — never ISO, never US.
 * ISO is for storage only.
 */
export function formatUk(date: IsoDate): string {
  const d = toUtc(date);
  return `${DAY_NAMES[d.getUTCDay()]} ${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}
